<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { GameItem } from '~/utils/perfilOficialConfigs';
import {
  extractProviderSlug,
  getProviderImage,
  getProviderStyle,
} from '~/utils/providerImages';
import PerfilOficialSwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';
import 'swiper/css';

interface TabData {
  title: string;
  child: GameItem[];
  icon?: string;
  slug?: string;
  see_more_url?: string | null;
}

type SortByOption = '' | 'name' | 'turnover';

const props = withDefaults(
  defineProps<{
    tabs: TabData[];
    headerTitle?: string;
    headerIconPath?: string;
    headerSeeMoreUrl?: string;
    isTabVisible?: boolean;
  }>(),
  {
    headerTitle: undefined,
    headerIconPath: undefined,
    headerSeeMoreUrl: undefined,
    isTabVisible: true,
  },
);

const ITEMS_PER_PAGE_MOBILE = 9;
const ITEMS_PER_PAGE_TABLET = 12;
const ITEMS_PER_PAGE_DESKTOP = 24;

const screenSize = ref<'mobile' | 'tablet' | 'desktop'>('mobile');

function updateBreakpoint() {
  const w = window.innerWidth;

  if (w >= 1024) screenSize.value = 'desktop';
  else if (w >= 768) screenSize.value = 'tablet';
  else screenSize.value = 'mobile';
}

if (typeof window !== 'undefined') {
  updateBreakpoint();
  window.addEventListener('resize', updateBreakpoint);
}

const itemsPerPage = computed(() => {
  if (screenSize.value === 'desktop') return ITEMS_PER_PAGE_DESKTOP;
  if (screenSize.value === 'tablet') return ITEMS_PER_PAGE_TABLET;

  return ITEMS_PER_PAGE_MOBILE;
});

const activeTabIndex = ref(0);
const searchQuery = ref('');
const currentPage = ref(1);

// Sort state
const selectedSortBy = ref<SortByOption>('');
const selectedSortDir = ref<'asc' | 'desc'>('asc');
const isSortDropdownOpen = ref(false);
const sortDropdownRef = ref<HTMLElement | null>(null);

const sortByOptions: { slug: SortByOption; name: string }[] = [
  { slug: '', name: 'Sem filtro' },
  { slug: 'name', name: 'Nome' },
  { slug: 'turnover', name: 'Apostado Hoje' },
];

// Toggle sort dropdown
function toggleSortDropdown() {
  isSortDropdownOpen.value = !isSortDropdownOpen.value;
}

// Close dropdown when clicking outside
function closeSortDropdown(event: MouseEvent) {
  if (
    sortDropdownRef.value &&
    !sortDropdownRef.value.contains(event.target as Node)
  ) {
    isSortDropdownOpen.value = false;
  }
}

// Toggle sort direction
function toggleSortDirection() {
  selectedSortDir.value = selectedSortDir.value === 'asc' ? 'desc' : 'asc';
  currentPage.value = 1;
}

// Select sort option
function selectSortBy(slug: SortByOption) {
  selectedSortBy.value = slug;
  isSortDropdownOpen.value = false;
  currentPage.value = 1;

  if (slug === 'turnover') {
    selectedSortDir.value = 'desc';
  } else {
    selectedSortDir.value = 'asc';
  }
}

// Lifecycle - add click listener for closing dropdown
if (typeof window !== 'undefined') {
  watch(isSortDropdownOpen, (isOpen) => {
    if (isOpen) {
      document.addEventListener('click', closeSortDropdown);
    } else {
      document.removeEventListener('click', closeSortDropdown);
    }
  });
}

// Debounce simples
let searchTimeout: ReturnType<typeof setTimeout>;

function onSearchInput(value: string) {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchQuery.value = value;
    currentPage.value = 1;
  }, 300);
}

function clearSearch() {
  searchQuery.value = '';
  currentPage.value = 1;
}

// Extrai label amigavel do titulo da tab (ex: "Todos-slots-padrinho" -> "Todos")
function getTabLabel(title: string): string {
  const firstPart = title.split(/[-_]/)[0] ?? title;

  return firstPart.charAt(0).toUpperCase() + firstPart.slice(1).toLowerCase();
}

// Tabs filtradas (com games) + provider extraido do slug
const componentTabs = computed(() => {
  return props.tabs
    .filter((tab) => tab.child && tab.child.length > 0)
    .map((tab) => {
      const providerSlug = tab.slug ? extractProviderSlug(tab.slug) : undefined;

      return {
        ...tab,
        providerSlug,
        providerImage: providerSlug
          ? getProviderImage(providerSlug)
          : undefined,
        label: providerSlug ? tab.title : getTabLabel(tab.title),
      };
    });
});

// Games da tab ativa, filtrados pela busca e ordenados
const filteredGames = computed(() => {
  const activeTab = componentTabs.value[activeTabIndex.value];

  if (!activeTab) return [];

  let games = [...(activeTab.child || [])];

  // Filtro de busca
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();

    games = games.filter((game) => game.name.toLowerCase().includes(query));
  }

  // Ordenacao
  if (selectedSortBy.value === 'name') {
    games.sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, 'pt-BR', {
        sensitivity: 'base',
      });

      return selectedSortDir.value === 'asc' ? cmp : -cmp;
    });
  } else if (selectedSortBy.value === 'turnover') {
    games.sort((a, b) => {
      const aVal = Number(a.turnover ?? 0) || 0;
      const bVal = Number(b.turnover ?? 0) || 0;
      const cmp = aVal - bVal;

      return selectedSortDir.value === 'asc' ? cmp : -cmp;
    });
  } else if (selectedSortDir.value === 'desc') {
    // Sem filtro + desc = ordem inversa da API
    games.reverse();
  }

  return games;
});

// Subset paginado
const paginatedGames = computed(() => {
  return filteredGames.value.slice(0, currentPage.value * itemsPerPage.value);
});

const totalGames = computed(() => filteredGames.value.length);
const displayedGames = computed(() => paginatedGames.value.length);

// Pagination object for ApostatudoPagination component
const pagination = computed(() => {
  const lastPage = Math.max(
    1,
    Math.ceil(totalGames.value / itemsPerPage.value),
  );

  return {
    total: totalGames.value,
    to: displayedGames.value,
    currentPage: currentPage.value,
    lastPage,
  };
});

// URL dinamica do "Ver Mais" baseada na tab ativa
const dynamicSeeMoreUrl = computed(() => {
  const activeTab = componentTabs.value[activeTabIndex.value];

  if (activeTab?.see_more_url) return activeTab.see_more_url;
  if (activeTab?.slug) return `/games/category/${activeTab.slug}`;

  return props.headerSeeMoreUrl;
});

// Icone dinamico baseado na tab ativa
const dynamicIconPath = computed(() => {
  const activeTab = componentTabs.value[activeTabIndex.value];

  if (activeTab?.icon) return activeTab.icon;

  return props.headerIconPath;
});

function selectTab(index: number) {
  if (activeTabIndex.value === index) return;
  activeTabIndex.value = index;
  searchQuery.value = '';
  currentPage.value = 1;
}

function loadMore() {
  currentPage.value++;
}
</script>

<template>
  <div class="apiTabsGrid">
    <!-- Header -->
    <PerfilOficialSwiperHeader
      v-if="headerTitle"
      :icon-path="dynamicIconPath"
      :see-more-url="dynamicSeeMoreUrl"
      :title="headerTitle"
    />

    <!-- Provider Logo Tabs -->
    <div
      v-if="isTabVisible && componentTabs.length > 1"
      class="apiTabsGrid__tabs"
    >
      <Swiper
        class="w-full tab-glow--swiper"
        :free-mode="true"
        :slides-per-view="'auto'"
        :space-between="8"
      >
        <SwiperSlide
          v-for="(tab, index) in componentTabs"
          :key="index"
          class="w-auto flex-shrink-0"
        >
          <button
            class="apiTabsGrid__tab-button"
            :class="{
              active: activeTabIndex === index,
              'tab-glow-active': activeTabIndex === index,
            }"
            @click="selectTab(index)"
          >
            <div
              v-if="tab.providerImage"
              class="apiTabsGrid__provider-image-wrapper"
            >
              <NuxtImg
                :alt="tab.title"
                class="apiTabsGrid__provider-image"
                :draggable="false"
                :src="tab.providerImage"
                :style="
                  tab.providerSlug ? getProviderStyle(tab.providerSlug) : {}
                "
              />
            </div>
            <span
              v-else
              class="apiTabsGrid__tab-text"
            >
              {{ tab.label }}
            </span>
          </button>
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- Search Bar + Sort Controls -->
    <div class="apiTabsGrid__search">
      <div class="apiTabsGrid__search-row">
        <!-- Search Input -->
        <div class="apiTabsGrid__search-input-wrapper">
          <input
            class="apiTabsGrid__search-input"
            placeholder="Procure jogos"
            :value="searchQuery"
            @input="
              onSearchInput(($event.target as HTMLInputElement)?.value || '')
            "
          />
          <NuxtIcon
            v-if="searchQuery"
            class="text-bg-primary/70 cursor-pointer text-sm w-5 p-0 m-0 ml-2"
            name="close"
            @click.prevent="clearSearch"
          />
          <NuxtIcon
            class="text-bg-primary cursor-pointer text-[18px] ml-2"
            filled
            name="apostatudo/search-lens"
          />
        </div>

        <!-- Sort Dropdown Button -->
        <div
          ref="sortDropdownRef"
          class="apiTabsGrid__sort-dropdown-wrapper"
        >
          <button
            aria-label="Menu de ordenacao"
            class="apiTabsGrid__sort-button"
            @click.stop="toggleSortDropdown"
          >
            <NuxtIcon
              class="self-center"
              name="filter/bars"
            />
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="isSortDropdownOpen"
            class="apiTabsGrid__sort-dropdown"
          >
            <NuxtIcon
              class="apiTabsGrid__sort-dropdown-arrow"
              name="filter/dropdown-arrow"
            />

            <div class="apiTabsGrid__sort-dropdown-content">
              <button
                v-for="option in sortByOptions"
                :key="option.slug"
                class="apiTabsGrid__sort-option"
                :class="{ active: selectedSortBy === option.slug }"
                @click="selectSortBy(option.slug)"
              >
                {{ option.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- Sort Direction Toggle Button -->
        <button
          aria-label="Alternar direcao de ordenacao"
          class="apiTabsGrid__sort-button"
          @click.stop="toggleSortDirection"
        >
          <NuxtIcon
            class="self-center text-[20px]"
            :name="selectedSortDir === 'asc' ? 'filter/az' : 'filter/za'"
          />
        </button>
      </div>
    </div>

    <!-- Games Grid -->
    <div class="apiTabsGrid__games">
      <CasinoMediaApostatudo
        v-for="(game, idx) in paginatedGames"
        :key="game.id || game.slug"
        :game="game as Game"
        :index="idx"
      />
    </div>

    <!-- Empty state -->
    <div
      v-if="totalGames === 0 && searchQuery"
      class="apiTabsGrid__empty"
    >
      <p>Nenhum jogo encontrado para "{{ searchQuery }}"</p>
    </div>

    <!-- Pagination -->
    <CasinoGamesApostatudoPagination
      :pagination="pagination"
      @load-more-games="loadMore"
    />
  </div>
</template>

<style>
.apiTabsGrid {
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 0;
}

/* --- Tabs --- */
.apiTabsGrid__tabs {
  margin-bottom: 12px;
  overflow: visible;
}

.apiTabsGrid__tab-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  height: 32px;
  border-radius: 12px;
  background: rgba(63, 63, 70, 0.4);
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.1s;
}

.apiTabsGrid__tab-button:active {
  transform: scale(0.95);
}

.apiTabsGrid__tab-button.active {
  border-color: var(--accent-color, #1414dd);
}

.apiTabsGrid__provider-image-wrapper {
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.apiTabsGrid__provider-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.apiTabsGrid__tab-text {
  font-size: 11.051px;
  font-weight: 700;
  line-height: 11.051px;
  color: white;
}

/* --- Search Bar + Sort --- */
.apiTabsGrid__search {
  margin-bottom: 12px;
}

.apiTabsGrid__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.apiTabsGrid__search-input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  background: #e4e4e7;
  border: 2px solid #a1a1aa;
  border-radius: 14px;
  height: 36px;
  padding: 0 8px;
}

.apiTabsGrid__search-input {
  width: 100%;
  height: 36px;
  background: transparent;
  color: black;
  font-weight: 800;
  font-size: 12px;
  padding: 0 8px;
  outline: none;
}

.apiTabsGrid__search-input::placeholder {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 800;
}

/* --- Sort Buttons --- */
.apiTabsGrid__sort-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background: rgba(63, 63, 70, 0.4);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: transform 0.1s;
  flex-shrink: 0;
}

.apiTabsGrid__sort-button:hover {
  background: rgba(63, 63, 70, 0.6);
}

.apiTabsGrid__sort-button:active {
  transform: scale(0.95);
  background: rgba(63, 63, 70, 0.8);
}

/* --- Sort Dropdown --- */
.apiTabsGrid__sort-dropdown-wrapper {
  position: relative;
}

.apiTabsGrid__sort-dropdown {
  position: absolute;
  right: 0;
  top: 48px;
  width: 160px;
  background: #0d1323;
  backdrop-filter: blur(12px);
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  z-index: 50;
}

.apiTabsGrid__sort-dropdown-arrow {
  position: absolute;
  top: -7px;
  right: 10px;
  width: 16px;
  height: 16px;
  color: #0d1323;
}

.apiTabsGrid__sort-dropdown-content {
  position: relative;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.apiTabsGrid__sort-option {
  width: 100%;
  padding: 8px 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: white;
  border-radius: 12px;
  background: rgba(63, 63, 70, 0.4);
  cursor: pointer;
  transition: background 0.2s;
}

.apiTabsGrid__sort-option:hover {
  background: rgba(63, 63, 70, 0.6);
}

.apiTabsGrid__sort-option.active {
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--accent-color, #1414dd) 70%, transparent),
    color-mix(in srgb, var(--accent-color, #1414dd) 20%, transparent)
  );
  border: 1px solid var(--accent-color, #1414dd);
}

/* --- Games Grid --- */
.apiTabsGrid__games {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  /* Isola o stacking dos cards (ribbons/badges com z alto não vazam
     pra cima de modais teleportados ao body). */
  isolation: isolate;
}

.apiTabsGrid__game-card {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  aspect-ratio: 3 / 3.5;
}

.apiTabsGrid__game-image-wrapper {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 4px;
}

.apiTabsGrid__game-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* --- Empty state --- */
.apiTabsGrid__empty {
  text-align: center;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

/* --- Desktop --- */
@media (min-width: 768px) {
  .apiTabsGrid__games {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  .apiTabsGrid__tab-button {
    height: 44px;
    padding: 12px 24px;
  }
}

@media (min-width: 1024px) {
  .apiTabsGrid__games {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 16px;
  }
}
</style>
